import React from "react";
import { LANDING } from "../constants/testIds/landing";
import { Award, Compass, Quote } from "lucide-react";

export const TheBook = ({ t }) => {
  return (
    <section
      id="book"
      className="py-24 sm:py-32 lg:py-40 bg-[#e5d8cb] text-[#1c110e] border-t border-[#b58e70]/20 overflow-hidden relative"
    >
      {/* Background abstract element */}
      <div className="absolute right-0 top-1/4 w-[500px] h-[500px] rounded-full bg-[#8a2424]/5 blur-[120px] pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        
        {/* Asymmetrical Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Cover & Visual Accent */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative group max-w-sm sm:max-w-md w-full">
              {/* Elegant shadow background and frame */}
              <div className="absolute -inset-2 bg-gradient-to-tr from-[#8a2424] to-[#d97036] opacity-30 blur-lg group-hover:opacity-40 transition-opacity duration-500" />
              <div className="relative border border-[#b58e70]/30 bg-[#1f1612] p-3 sm:p-4 shadow-2xl">
                <img
                  src={`${process.env.PUBLIC_URL}/images/book-cover.jpg`}
                  alt="Buenos Aires für Tango Reisende - ein Leitfaden"
                  data-testid={LANDING.bookCoverImg}
                  className="w-full h-auto object-contain select-none"
                />
              </div>

              {/* Decorative Background Image behind the book frame */}
              <div className="absolute -bottom-8 -right-8 w-44 h-44 border border-[#b58e70]/20 overflow-hidden hidden sm:block z-[-1] opacity-60">
                <img
                  src={`${process.env.PUBLIC_URL}/images/book-decor.jpg`}
                  alt="Buenos Aires Street"
                  className="w-full h-full object-cover filter sepia brightness-50"
                />
              </div>
            </div>
          </div>

          {/* Right Column: Information & CTAs */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-3">
              <span className="font-sans text-xs sm:text-sm tracking-[0.3em] uppercase text-[#d97036] font-medium block">
                {t.book.title}
              </span>
              <h2
                data-testid={LANDING.bookTitle}
                className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1c110e] tracking-tight font-light leading-tight"
              >
                {t.book.subtitle}
              </h2>
              <span className="inline-block font-sans text-xs sm:text-sm tracking-wider text-[#b58e70] bg-[#8a2424]/10 border border-[#b58e70]/20 px-3 py-1 mt-2">
                {t.book.isbn}
              </span>
            </div>

            <div className="space-y-6 font-sans text-sm sm:text-base text-[#5a4b46] leading-relaxed font-light">
              <p>{t.book.description1}</p>
              <p>{t.book.description2}</p>
            </div>

            {/* Book highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 pb-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#8a2424]/10 border border-[#8a2424]/20 mt-1">
                  <Compass className="w-4 h-4 text-[#d97036]" />
                </div>
                <div>
                  <h5 className="font-serif text-base text-[#1c110e] font-medium mb-1">
                    {t.book.highlight1Title}
                  </h5>
                  <p className="font-sans text-xs text-[#5a4b46] font-light leading-relaxed">
                    {t.book.highlight1Text}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#8a2424]/10 border border-[#8a2424]/20 mt-1">
                  <Award className="w-4 h-4 text-[#d97036]" />
                </div>
                <div>
                  <h5 className="font-serif text-base text-[#1c110e] font-medium mb-1">
                    {t.book.highlight2Title}
                  </h5>
                  <p className="font-sans text-xs text-[#5a4b46] font-light leading-relaxed">
                    {t.book.highlight2Text}
                  </p>
                </div>
              </div>
            </div>

            {/* Rezension */}
            <div className="bg-[#efe7dd] border border-[#b58e70]/30 p-6 sm:p-8 space-y-4">
              <div className="flex items-start gap-3">
                <Quote className="w-6 h-6 text-[#d97036] shrink-0 -mt-1" />
                <p className="font-sans text-xs sm:text-sm tracking-wider uppercase text-[#8a2424] font-semibold">
                  {t.book.reviewLabel}
                </p>
              </div>
              <p className="font-serif text-sm sm:text-base text-[#5a4b46] leading-relaxed italic whitespace-pre-line">
                {t.book.reviewText}
              </p>
              <p className="font-sans text-xs text-[#b58e70] tracking-wide text-right">
                — {t.book.reviewSource}
              </p>
            </div>

            {/* Kontakt-Hinweis */}
            <p className="font-sans text-sm sm:text-base text-[#5a4b46] leading-relaxed font-light">
              {t.book.contactNote}
            </p>

            {/* Verfügbarkeit */}
            <div className="pt-2">
              <p className="font-sans text-sm sm:text-base text-[#1c110e] font-medium leading-relaxed border-l-2 border-[#8a2424] pl-4">
                {t.book.availability}
              </p>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
